import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../context/ToastContext";
import { useNavigate, useParams } from "react-router-dom";
import FailureApi from "../../../services/FailureApi";
import ListOperationApi from "../../../services/OperationApi";
import { classNames } from "primereact/utils";

// Design System
import FormCard from "../../../components/FormCard";
import Input from "../../../components/Input/Input";

interface Option {
  id: string;
  name: string;
}

const FailureDetailsTab: React.FC = () => {
  const { t: translate } = useTranslation("form");
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const { failureId, fmeaId } = useParams<{ failureId: string; fmeaId: string }>();

  const descricaoRef = useRef<any>(null);
  const effectRef = useRef<any>(null);
  const causeDescriptionRef = useRef<any>(null);
  const severityRef = useRef<any>(null);
  const occurrenceRef = useRef<any>(null);
  const detectRef = useRef<any>(null);

  const [operations, setOperations] = useState<Option[]>([]);
  const [causes, setCauses] = useState<Option[]>([]);
  const [operation, setOperation] = useState<string>("");
  const [code, setCode] = useState("");
  const [version, setVersion] = useState("");
  const [descricao, setDescricao] = useState("");
  const [effect, setEffect] = useState("");
  const [cause, setCause] = useState<string>("");
  const [causeDescription, setCauseDescription] = useState("");
  const [severity, setSeverity] = useState("");
  const [occurrence, setOccurrence] = useState("");
  const [detectability, setDetectability] = useState("");
  const [rpn, setRpn] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const s = parseInt(severity || "0", 10);
    const o = parseInt(occurrence || "0", 10);
    const d = parseInt(detectability || "0", 10);
    if (!isNaN(s) && !isNaN(o) && !isNaN(d)) setRpn((s * o * d).toString());
  }, [severity, occurrence, detectability]);

  useEffect(() => {
    if (!failureId) return;
    const loadData = async (): Promise<void> => {
      try {
        const failureRes = await FailureApi.GetFailureResponseById(failureId);
        const f = failureRes.data;
        if (!f) throw new Error("Failure not found");
        const opsRes = await ListOperationApi.GetOperationsBySubLineId(f.sublineId);
        const causesRes = await FailureApi.GetAllCauses();

        setOperations(opsRes.data || []);
        setCauses(causesRes.data || []);

        const matchedOp = opsRes.data.find((o: any) => o.id === f.operationId);
        setOperation(matchedOp?.id || "");
        setCode(String(f.code));
        setVersion(String(f.version));
        setDescricao(f.description || "");
        setEffect(f.effect || "");
        const matchedCause = causesRes.data.find((c: any) => String(c.id) === String(f.causeId));
        setCause(matchedCause?.id || "");
        setCauseDescription(f.causeDescription || "");
        setSeverity(String(f.severity ?? ""));
        setOccurrence(String(f.occurrence ?? ""));
        setDetectability(String(f.detectability ?? ""));
        setRpn(String(f.rpn ?? ""));
      } catch {
        displayToast("error", translate("ErrorLoading"), "", false, 3000);
      }
    };
    loadData();
  }, [failureId, displayToast, translate]);

  const validate = (): boolean => {
    const newErr: Record<string, string> = {};
    if (!operation) newErr.operation = translate("SelectOperationRequired");
    if (!code.trim()) newErr.code = translate("CodeRequired");
    if (!version.trim()) newErr.version = translate("VersionRequired");
    if (!descricao.trim()) newErr.descricao = translate("DescriptionRequired");
    if (!effect.trim()) newErr.effect = translate("EffectRequired");
    if (!cause) newErr.cause = translate("CauseRequired");
    if (!causeDescription.trim()) newErr.causeDescription = translate("CauseDescriptionRequired");
    if (!severity.trim()) newErr.severity = translate("SeverityRequired");
    if (!occurrence.trim()) newErr.occurrence = translate("OccurrenceRequired");
    if (!detectability.trim()) newErr.detectability = translate("DetectabilityRequired");
    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  const handleSave = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();
    if (!validate()) {
      if (!descricao.trim()) descricaoRef.current?.focus();
      else if (!effect.trim()) effectRef.current?.focus();
      else if (!causeDescription.trim()) causeDescriptionRef.current?.focus();
      else if (!severity.trim()) severityRef.current?.focus();
      else if (!occurrence.trim()) occurrenceRef.current?.focus();
      else if (!detectability.trim()) detectRef.current?.focus();
      return;
    }
    if (!failureId) {
      displayToast("error", translate("Error"), translate("FailureIdRequired"), false, 3000);
      return;
    }
    try {
      const causeObj = causes.find((c) => c.id === cause);
      const payload: any = {
        id: failureId,
        description: descricao,
        effect: effect,
        causeId: causeObj?.id,
        causeDescription: causeDescription,
        severity: Number(severity),
        occurrence: Number(occurrence),
        detectability: Number(detectability),
      };
      await FailureApi.Update(payload);
      displayToast("success", translate("Success"), translate("FailureUpdated"), false, 3000);
      navigate(`/fmeas/${fmeaId}/failures`);
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || translate("ErrorUnexpected");
      displayToast("error", translate("Error"), msg, false, 3000);
    }
  };

  const causeObj = causes.find((c) => c.id === cause) || null;

  return (
    <FormCard
      title={translate("FailureDetails")}
      icon="warning"
      submitButtonLabel={translate("Save")}
      onSubmit={handleSave}
      numColumns={12}
    >
      {/* Operation (disabled - read only) */}
      <div style={{ gridColumn: "span 12" }}>
        <Input
          type="select"
          label={translate("selectOperation")}
          value={operation}
          options={operations.map((o) => ({ label: o.name, value: o.id }))}
          optionLabel="label"
          optionValue="value"
          disabled
          placeholder={translate("selectOperation")}
          className={classNames("w-full", { "p-invalid border-red": errors.operation })}
          onChange={() => {}}
        />
        {errors.operation && (
          <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.operation}</small>
        )}
      </div>

      {/* Code & Version (read only) */}
      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("code")}
          value={code}
          disabled
          className={classNames("w-full", { "p-invalid border-red": errors.code })}
          onChange={() => {}}
        />
        {errors.code && <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.code}</small>}
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("version")}
          value={version}
          disabled
          className={classNames("w-full", { "p-invalid border-red": errors.version })}
          onChange={() => {}}
        />
        {errors.version && <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.version}</small>}
      </div>

      {/* Description */}
      <div style={{ gridColumn: "span 12" }}>
        <Input
          ref={descricaoRef}
          type="textArea"
          label={translate("Description")}
          value={descricao}
          required
          rows={3}
          className={classNames("w-full", { "p-invalid border-red": errors.descricao })}
          onChange={(val: any) => {
            setDescricao(val);
            setErrors((prev) => ({ ...prev, descricao: "" }));
          }}
        />
        {errors.descricao && <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.descricao}</small>}
      </div>

      {/* Effect & Cause */}
      <div style={{ gridColumn: "span 6" }}>
        <Input
          ref={effectRef}
          type="text"
          label={translate("effect")}
          value={effect}
          required
          className={classNames("w-full", { "p-invalid border-red": errors.effect })}
          onChange={(val: any) => {
            setEffect(val);
            setErrors((prev) => ({ ...prev, effect: "" }));
          }}
        />
        {errors.effect && <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.effect}</small>}
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="select"
          label={translate("cause")}
          value={cause}
          options={causes.map((c) => ({ label: c.name, value: c.id }))}
          optionLabel="label"
          optionValue="value"
          required
          placeholder={translate("cause")}
          className={classNames("w-full", { "p-invalid border-red": errors.cause })}
          onChange={(val: any) => {
            setCause(val);
            setErrors((prev) => ({ ...prev, cause: "" }));
          }}
        />
        {errors.cause && <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.cause}</small>}
      </div>

      {/* Cause Description (conditional) */}
      {causeObj && (
        <div style={{ gridColumn: "span 12" }}>
          <Input
            ref={causeDescriptionRef}
            type="textArea"
            label={translate("causeDescription")}
            value={causeDescription}
            required
            rows={2}
            className={classNames("w-full", { "p-invalid border-red": errors.causeDescription })}
            onChange={(val: any) => {
              setCauseDescription(val);
              setErrors((prev) => ({ ...prev, causeDescription: "" }));
            }}
          />
          {errors.causeDescription && <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.causeDescription}</small>}
        </div>
      )}

      {/* Numeric: Severity / Occurrence / Detectability / RPN */}
      <div style={{ gridColumn: "span 3" }}>
        <Input
          ref={severityRef}
          type="text"
          label={translate("severity")}
          value={severity}
          required
          placeholder="1–10"
          className={classNames("w-full", { "p-invalid border-red": errors.severity })}
          onChange={(val: any) => {
            setSeverity(val);
            setErrors((prev) => ({ ...prev, severity: "" }));
          }}
        />
        {errors.severity && <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.severity}</small>}
      </div>

      <div style={{ gridColumn: "span 3" }}>
        <Input
          ref={occurrenceRef}
          type="text"
          label={translate("occurrence")}
          value={occurrence}
          required
          placeholder="1–10"
          className={classNames("w-full", { "p-invalid border-red": errors.occurrence })}
          onChange={(val: any) => {
            setOccurrence(val);
            setErrors((prev) => ({ ...prev, occurrence: "" }));
          }}
        />
        {errors.occurrence && <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.occurrence}</small>}
      </div>

      <div style={{ gridColumn: "span 3" }}>
        <Input
          ref={detectRef}
          type="text"
          label={translate("detectability")}
          value={detectability}
          required
          placeholder="1–10"
          className={classNames("w-full", { "p-invalid border-red": errors.detectability })}
          onChange={(val: any) => {
            setDetectability(val);
            setErrors((prev) => ({ ...prev, detectability: "" }));
          }}
        />
        {errors.detectability && <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.detectability}</small>}
      </div>

      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label="RPN"
          value={rpn}
          disabled
          onChange={() => {}}
        />
        <small style={{ color: "var(--color-neutral-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>
          {translate("rpn")}
        </small>
      </div>
    </FormCard>
  );
};

export default FailureDetailsTab;
